import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface OrderItemVariant {
  id?: string;
  size?: string;
  color?: string;
  colorHex?: string;
  priceModifier?: number;
  sku?: string;
}

export interface OrderItem {
  id: string; // productId
  name: string;
  // Pricing captured at time of purchase
  price: number; // final unit price charged
  basePrice?: number;
  priceModifier?: number;

  quantity: number;
  image: string;
  category?: string;

  // Variant snapshot for the order line
  variant?: OrderItemVariant;
}

export interface Order {
  id: string;
  userId: string;
  items: OrderItem[];
  total: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  paymentStatus: 'pending' | 'completed' | 'failed' | 'half_paid'; // <-- added "half_paid"
  paymentId?: string;
  paymentMethod?: 'razorpay' | 'cod';
  transactionId?: string;
  codAdvanceAmount?: number; // <-- added field

  shippingAddress: {
    name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface OrdersState {
  orders: Order[];
  isLoading: boolean;
  error: string | null;
}

const initialState: OrdersState = {
  orders: [],
  isLoading: false,
  error: null,
};

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setOrders: (state, action: PayloadAction<Order[]>) => {
      state.orders = action.payload;
    },
    addOrder: (state, action: PayloadAction<Order>) => {
      state.orders.unshift({
        ...action.payload,
        paymentStatus: action.payload.paymentStatus || 'pending',
        codAdvanceAmount: action.payload.codAdvanceAmount ?? null,
      });
    },
    updateOrderStatus: (state, action: PayloadAction<{ id: string; status: Order['status'] }>) => {
      const order = state.orders.find(order => order.id === action.payload.id);
      if (order) {
        order.status = action.payload.status;
        order.updatedAt = new Date().toISOString();
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { setOrders, addOrder, updateOrderStatus, setLoading, setError } = ordersSlice.actions;
export default ordersSlice.reducer;